import { DynamicDrawUsage, InstancedMesh, Object3D } from 'three';
import Experience from '../Experience.js';
import Repulsor from './Repulsor.js';

/**
 * Base for the shape fields on the home scene.
 *
 * Each field used to be a Group of one Mesh per shape, every one carrying its
 * own material instance - so ~74 objects and ~74 draw calls a frame for
 * geometry that never varies. Each field is now a single InstancedMesh with one
 * shared material.
 *
 * Animation still needs real position / rotation / scale objects (GSAP tweens
 * them directly, the orbit field integrates them by hand), so every instance
 * keeps a lightweight Object3D proxy. syncMatrices() folds those proxies into
 * the instance matrix buffer, which is the only per-frame work the old version
 * got for free from the scene graph.
 *
 * A field can also be given a repulsion config, in which case the cursor shoves
 * instances aside. That shove is a separate offset applied as the matrices are
 * built, so whatever owns the base position - a GSAP tween here, an orbit in
 * OrbitField - never sees it and never has to be stopped and restarted.
 */
export default class InstancedShapes {
    constructor(geometry, material, amount, repulsion) {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.isMobile = this.experience.world.isMobile();

        this.mesh = new InstancedMesh(geometry, material, amount);
        this.mesh.instanceMatrix.setUsage(DynamicDrawUsage);
        // The animation carries instances far outside the source geometry's
        // bounds, so per-object culling would pop them out. One draw call is
        // cheap enough that always drawing is the safer trade.
        this.mesh.frustumCulled = false;

        this.dummies = [];
        for (let i = 0; i < amount; i++) {
            this.dummies.push(new Object3D());
        }

        this.pointer = this.experience.pointer;
        this.repulsor = repulsion ? new Repulsor(amount, repulsion) : null;

        this.scene.add(this.mesh);
    }

    /** Run a placement function over every instance, then push the matrices. */
    place(fn) {
        for (let i = 0; i < this.dummies.length; i++) {
            fn(this.dummies[i], i);
        }
        this.syncMatrices();
    }

    // Tween targets, in the same shape the old group.children.map(...) produced.
    get positions() {
        return this.dummies.map(d => d.position);
    }

    get rotations() {
        return this.dummies.map(d => d.rotation);
    }

    get scales() {
        return this.dummies.map(d => d.scale);
    }

    /** Advance the cursor push, if this field has any. */
    repel(delta) {
        if (!this.repulsor) return;
        this.repulsor.stepDummies(delta, this.pointer, this.dummies);
    }

    syncMatrices() {
        const offsets = this.repulsor && this.repulsor.offsets;

        for (let i = 0; i < this.dummies.length; i++) {
            const dummy = this.dummies[i];
            dummy.updateMatrix();
            if (offsets) {
                // 12/13/14 are the translation column of the composed matrix,
                // so the offset goes in without disturbing dummy.position.
                const e = dummy.matrix.elements;
                e[12] += offsets[i * 3];
                e[13] += offsets[i * 3 + 1];
                e[14] += offsets[i * 3 + 2];
            }
            this.mesh.setMatrixAt(i, dummy.matrix);
        }
        this.mesh.instanceMatrix.needsUpdate = true;
    }

    update(delta) {
        if (!this.mesh.visible) {
            // Hidden fields stop updating, so let go of any half-finished shove
            // rather than freezing it until the field comes back.
            if (this.repulsor) this.repulsor.reset();
            return;
        }
        this.repel(delta);
        this.syncMatrices();
    }
}

/**
 * Placement for the central cluster: a random point in a `spread`-wide cube
 * about the origin, random orientation, random scale up to `maxScale`.
 */
export const scatter = (spread, maxScale) => (dummy) => {
    dummy.position.set(
        (Math.random() - 0.5) * spread,
        (Math.random() - 0.5) * spread,
        (Math.random() - 0.5) * spread
    );
    dummy.rotation.x = Math.random() * Math.PI;
    dummy.rotation.y = Math.random() * Math.PI;
    const s = Math.random() * maxScale;
    dummy.scale.set(s, s, s);
};
